import { AfterViewInit, Directive, ElementRef, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';

@Directive({
  selector: '[aiIosKeyboardCaretFix]',

})
export class IosKeyboardCaretFixDirective implements OnInit, AfterViewInit, OnDestroy {
  private readonly el = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
  private readonly autosize = inject(CdkTextareaAutosize, { optional: true });
  private listenerHandle: PluginListenerHandle | null = null;
  private isFocused = false;
  private _rows = 10;
  private _destroyed = false;

  @Input('aiIosKeyboardCaretFix')
  set rows(val: number | '') {
    if (val !== '') {
      this._rows = val;
    }
  }

  @HostListener('focus')
  onFocus(): void {
    this.isFocused = true;
  }

  @HostListener('blur')
  onBlur(): void {
    this.isFocused = false;
  }

  async ngOnInit(): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return;
    }
    const handle = await Keyboard.addListener('keyboardDidShow', () => {
      requestAnimationFrame(() => this.correctCaretScroll());
    });
    if (this._destroyed) {
      handle.remove();
    } else {
      this.listenerHandle = handle;
    }
  }

  ngAfterViewInit(): void {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return;
    }
    // Disable cdkTextareaAutosize on iOS — height growth triggers iOS WKWebView to
    // scroll the page to show the textarea top on every keystroke.
    if (this.autosize) {
      this.autosize.enabled = false;
    }
    const textarea = this.el.nativeElement;
    textarea.rows = this._rows;
    textarea.style.overflowY = 'auto';
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this.listenerHandle?.remove();
  }

  private correctCaretScroll(): void {
    if (!this.isFocused) {
      return;
    }

    const textarea = this.el.nativeElement;
    const caretOffset = getCaretOffset(textarea, textarea.selectionStart ?? 0);
    const rect = textarea.getBoundingClientRect();

    // visualViewport.height always reflects the actual visible area above the keyboard,
    // even when window.innerHeight transiently reports the full screen height during
    // keyboard animations (KeyboardResize.Native).
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const visibleTextareaBottom = viewportHeight - rect.top;

    const caretAbsTop = caretOffset.top - textarea.scrollTop;
    const caretAbsBottom = caretAbsTop + caretOffset.height;

    if (caretAbsBottom > visibleTextareaBottom) {
      textarea.scrollTop = caretOffset.top - visibleTextareaBottom + caretOffset.height + 8;
    } else if (caretAbsTop < 0) {
      textarea.scrollTop = caretOffset.top - 8;
    }
  }
}

/**
 * Computes the pixel offset of the caret position within a textarea's scrollable content
 * using the mirror-div technique. Uses textContent (not innerText) to avoid HTML-encoding
 * issues with < > & characters common in AI prompt content.
 */
function getCaretOffset(textarea: HTMLTextAreaElement, position: number): { top: number; height: number } {
  const computed = window.getComputedStyle(textarea);

  const mirror = document.createElement('div');
  document.body.appendChild(mirror);

  const style = mirror.style;
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.top = '0';
  style.left = '0';
  style.whiteSpace = 'pre-wrap';
  style.wordWrap = 'break-word';

  const copyProps = [
    'boxSizing',
    'width',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontFamily',
    'fontSize',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'letterSpacing',
    'lineHeight',
    'textDecoration',
    'textIndent',
    'textTransform',
    'wordSpacing',
    'overflowWrap',
  ] as const;

  for (const prop of copyProps) {
    style[prop] = computed[prop];
  }

  mirror.textContent = textarea.value.substring(0, position);

  // Use a zero-width space so span.offsetHeight equals exactly one line height.
  const span = document.createElement('span');
  span.textContent = '\u200b';
  mirror.appendChild(span);

  const result = {
    top: span.offsetTop + parseInt(computed.borderTopWidth, 10),
    height: span.offsetHeight,
  };

  document.body.removeChild(mirror);
  return result;
}
